import { h } from '@/lib/dom';
import { Card } from '@/components/Card';
import { Skeleton, SkeletonList } from '@/components/Skeleton';
import { PhasesBar, PHASE_COLORS } from '@/components/PhasesBar';
import { SyncBadge } from '@/components/SyncBadge';
import { WindowSwitcher } from '@/components/WindowSwitcher';
import { chartsSection } from '@/components/ChartsSection';
import type { ChartSpec } from '@/components/ChartsSection';
import { TopTile as TopTileEl } from '@/components/TopTile';
import { DeepDiveTile } from '@/components/DeepDiveTile';
import { loadUPlot } from '@/lib/uplot-lazy';
import { registerCleanup } from '@/router';
import type uPlot from 'uplot';
import { data } from '@/lib/data';
import { formatDate, formatNumber, formatTime, parseDate, sliceRecent, WINDOW_LABELS, windowDays } from '@/lib/format';
import type { WindowKey } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { errorRetry } from '@/lib/states';
import type {
  IntegrationSummary,
  OuraDetailPayload,
  OuraLastNight,
  OuraRecentDay,
  OuraWindowStats,
} from '@/api/types';
import { pageLayout } from './layout';

const WINDOWS: WindowKey[] = ['7d', '30d', '90d'];

export async function renderSleepPage(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }

  let currentWindow: WindowKey = '7d';
  let detail: IntegrationSummary<OuraDetailPayload> | null = null;

  const content = h('div', { id: 'sleep-content', class: 'sleep-page' }, [
    h('div', { class: 'flex flex-col gap-6' }, [
      Skeleton({ variant: 'rect', height: '72px' }),
      SkeletonList(3),
      Skeleton({ variant: 'chart', height: '220px' }),
    ]),
  ]);

  const root = pageLayout({
    user,
    active: 'sleep',
    children: [content],
  });

  const onWindowChange = (w: WindowKey) => { currentWindow = w; render(); };

  const render = () => {
    if (!detail) return;
    content.innerHTML = '';
    content.append(...buildSections(detail, currentWindow, onWindowChange));
  };

  void (async () => {
    try {
      detail = await data.getOuraDetail();
      render();
    } catch (err) {
      content.innerHTML = '';
      content.appendChild(errorRetry(err, 'Не удалось загрузить данные сна.', '/sleep'));
    }
  })();

  return root;
}

function buildSections(
  detail: IntegrationSummary<OuraDetailPayload>,
  window: WindowKey,
  onWindowChange: (w: WindowKey) => void,
): Node[] {
  const payload = detail.payload;
  const header = sleepHeader(detail, window, onWindowChange);

  if (payload.error) {
    return [header, errorCard(payload.error)];
  }

  if (!payload.windows) {
    return [header, notConnectedCard(detail)];
  }

  const stats = payload.windows[window];
  const allRecent = payload.recent ?? [];
  const recent = sliceRecent(allRecent, window);
  const lastNight = payload.last_night ?? null;

  return [
    header,
    topStatsRow(stats),
    detailedGrid(stats, window),
    phasesSection(stats, recent),
    lastNightCard(lastNight),
    chartsGrid(recent, window),
  ];
}

function sleepHeader(
  detail: IntegrationSummary<OuraDetailPayload>,
  window: WindowKey,
  onWindowChange: (w: WindowKey) => void,
): HTMLElement {
  return h('header', { class: 'sleep-header' }, [
    h('div', { class: 'sleep-titles' }, [
      h('h1', { class: 'sleep-title' }, 'Сон · OURA'),
      h('p', { class: 'sleep-meta' },
        `Окно до 90 дней · ${detail.window_start ?? ''}–${detail.window_end ?? ''}`),
    ]),
    h('div', { class: 'sleep-header-controls' }, [
      h('div', { class: 'sleep-window-switcher' },
        WindowSwitcher({
          current: window,
          windows: WINDOWS,
          labelFor: (w) => WINDOW_LABELS[w],
          onChange: onWindowChange,
        })),
      h('div', { class: 'sleep-sync' }, SyncBadge(detail)),
    ]),
  ]);
}

function notConnectedCard(detail: IntegrationSummary<OuraDetailPayload>): HTMLElement {
  const msg = detail.payload.message;
  return Card({
    title: 'OURA не подключена',
    children: [
      h('p', null, msg ?? 'Токен OURA не задан.'),
      h('p', { class: 'text-tertiary mt-2' },
        'Чтобы появились данные сна — задайте OURA_TOKEN в серверном .env и перезапустите backend.'),
    ],
  });
}

function errorCard(code: string): HTMLElement {
  const label = code === 'token_invalid'
    ? 'Токен OURA отозван или устарел. Обновите OURA_TOKEN.'
    : code === 'network_error'
      ? 'Не удалось связаться с OURA API.'
      : `Ошибка OURA (${code}).`;
  return Card({
    title: 'Ошибка загрузки',
    children: h('p', null, label),
  });
}

function topStatsRow(stats: OuraWindowStats): HTMLElement {
  const tiles = [
    TopTileEl({
      label: 'Среднее время сна',
      value: stats.avg_sleep_hours != null ? `${formatNumber(stats.avg_sleep_hours, 1)} ч` : '—',
      tooltip: 'Сколько в среднем спал за выбранный период. Учитывается только полноценный ночной сон — короткие дневные сны не считаются.',
    }),
    TopTileEl({
      label: 'Readiness',
      value: stats.avg_readiness != null ? formatNumber(stats.avg_readiness, 0) : '—',
      tooltip: 'OURA Readiness 0–100 — насколько организм готов к нагрузке с утра. Складывается из HRV, пульса покоя, температуры тела и качества сна. Здесь — среднее за период.',
    }),
    TopTileEl({
      label: 'HRV',
      value: stats.avg_hrv != null ? `${formatNumber(stats.avg_hrv, 0)} мс` : '—',
      tooltip: 'Вариабельность пульса во сне, в мс. Высокая HRV — признак хорошего восстановления и низкого стресса. Среднее за период.',
    }),
    TopTileEl({
      label: 'Пульс покоя',
      value: stats.avg_resting_hr != null ? `${formatNumber(stats.avg_resting_hr, 0)} уд/мин` : '—',
      tooltip: 'Средний пульс во сне. Чем ниже — тем лучше тренированность и восстановление.',
    }),
  ];

  return h('div', { class: 'sleep-top-grid' }, tiles);
}

const DETAIL_TOOLTIPS: Record<string, string> = {
  'Sleep score': 'OURA Sleep Score 0–100 — общая оценка качества ночи: длительность, эффективность, фазы, консистенция. Чем выше — тем лучше.',
  'Эффективность': 'Сколько процентов времени в кровати ушло на сон. 90%+ — норма, ниже — много пробуждений или долго не получается заснуть.',
  'Латентность': 'Сколько минут ушло на засыпание. 10–20 мин — норма; постоянно >30 мин — признак стресса или переутомления.',
  'Δt°': 'Отклонение температуры тела от обычного уровня. Плюс — нагрузка или начинающаяся болезнь; минус — глубокий покой.',
  'Беспокойство': 'Сколько раз за ночь были короткие пробуждения и движения. Чем меньше — тем спокойнее спал.',
};

function detailedGrid(stats: OuraWindowStats, window: WindowKey): HTMLElement {
  const tiles: Array<[string, string, string]> = [
    ['Sleep score', stats.avg_sleep_score != null ? formatNumber(stats.avg_sleep_score, 0) : '—', ''],
    ['Эффективность', stats.avg_efficiency != null ? formatNumber(stats.avg_efficiency, 1) : '—', stats.avg_efficiency != null ? '%' : ''],
    ['Латентность', stats.avg_latency_minutes != null ? formatNumber(stats.avg_latency_minutes, 0) : '—', stats.avg_latency_minutes != null ? 'мин' : ''],
    ['Δt°',
      stats.avg_temperature_deviation != null
        ? `${stats.avg_temperature_deviation > 0 ? '+' : ''}${formatNumber(stats.avg_temperature_deviation, 2)}`
        : '—',
      ''],
    ['Беспокойство', stats.avg_restfulness != null ? formatNumber(stats.avg_restfulness, 0) : '—', ''],
    ['Дней с данными', `${stats.days_with_data} / ${windowDays(window)}`, ''],
  ];

  return h('section', { class: 'sleep-section' }, [
    h('h2', { class: 'sleep-section-header' }, [
      h('span', null, '💤 Подробно'),
    ]),
    h('div', { class: 'deepdive-stat-grid' },
      tiles.map(([label, value, unit]) => DeepDiveTile(label, value, unit, DETAIL_TOOLTIPS[label]))),
  ]);
}

function phasesSection(stats: OuraWindowStats, recent: OuraRecentDay[]): HTMLElement {
  const phasesArea = h('div', {
    class: 'sleep-phases-chart',
    role: 'img',
    aria: { label: `Распределение фаз сна по дням (${recent.length})` },
  });

  if (!recent.length) {
    phasesArea.appendChild(h('div', { class: 'empty-state' }, 'Нет дневных данных в окне.'));
  } else {
    queueMicrotask(() => { void mountPhasesChart(phasesArea, recent); });
  }

  return h('section', { class: 'sleep-section' }, [
    h('h2', { class: 'sleep-section-header' }, [
      h('span', null, 'Фазы сна'),
    ]),
    h('p', { class: 'sleep-section-hint' },
      'Сверху — среднее распределение по фазам в окне. Снизу — абсолютные минуты по каждой ночи; цвета линий соответствуют легенде сверху.'),
    PhasesBar({
      segments: [
        { key: 'deep', label: 'Глубокий', pct: stats.avg_deep_pct, minutes: stats.avg_deep_minutes, color: PHASE_COLORS.deep },
        { key: 'rem', label: 'REM', pct: stats.avg_rem_pct, minutes: stats.avg_rem_minutes, color: PHASE_COLORS.rem },
        { key: 'light', label: 'Лёгкий', pct: stats.avg_light_pct, minutes: stats.avg_light_minutes, color: PHASE_COLORS.light },
        { key: 'awake', label: 'Бодрствование', pct: stats.avg_awake_pct, minutes: stats.avg_awake_minutes, color: PHASE_COLORS.awake },
      ],
    }),
    h('div', { class: 'sleep-phases-chart-wrap' }, phasesArea),
  ]);
}

const SHORT_MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function shortDateRu(tsSec: number): string {
  const d = new Date(tsSec * 1000);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
}

function fullDateRu(tsSec: number): string {
  const d = new Date(tsSec * 1000);
  return `${d.getDate()} ${SHORT_MONTHS_RU[d.getMonth()]} ${d.getFullYear()}`;
}

function readVar(name: string, fallback: string): string {
  if (typeof getComputedStyle === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

async function mountPhasesChart(area: HTMLElement, recent: OuraRecentDay[]): Promise<void> {
  if (!area.isConnected) {
    queueMicrotask(() => { void mountPhasesChart(area, recent); });
    return;
  }
  const uPlotCtor = await loadUPlot();
  if (!area.isConnected) return;

  // parseDate, а не UTC-полночь: подписи осей читают эти значения локальными
  // геттерами (SingleLineChart), поэтому западнее Гринвича день съезжал назад.
  const xs = recent.map((r) => (parseDate(r.day)?.getTime() ?? NaN) / 1000);
  const deep = recent.map((r) => r.deep_min ?? null);
  const rem = recent.map((r) => r.rem_min ?? null);
  const light = recent.map((r) => r.light_min ?? null);
  const awake = recent.map((r) => r.awake_min ?? null);

  const axisColor = readVar('--text-secondary', '#4A5562');
  const gridColor = readVar('--chart-grid', '#E4E7EB');

  const xMin = xs[0] ?? 0;
  const xMax = xs[xs.length - 1] ?? 0;
  const spanDays = Math.max(1, Math.round((xMax - xMin) / 86400));
  const stepDays = spanDays <= 7 ? 1 : spanDays <= 30 ? 3 : 10;

  const width = area.clientWidth || 600;
  const height = area.clientHeight || 220;

  const opts: uPlot.Options = {
    width,
    height,
    padding: [12, 12, 8, 44],
    legend: { show: false },
    cursor: { show: true, points: { show: true } },
    scales: {
      x: { time: true },
      y: { auto: true },
    },
    axes: [
      {
        stroke: axisColor,
        font: '11px ui-sans-serif, system-ui, sans-serif',
        grid: { stroke: gridColor, width: 1 },
        space: 70,
        splits: () => xs.filter((_, i) => i % stepDays === 0),
        values: (_u, splits) => splits.map((s) => shortDateRu(s)),
      },
      {
        label: 'мин',
        labelSize: 12,
        labelFont: '11px ui-sans-serif, system-ui, sans-serif',
        stroke: axisColor,
        font: '11px ui-sans-serif, system-ui, sans-serif',
        grid: { stroke: gridColor, width: 1 },
        size: 44,
      },
    ],
    series: [
      {},
      { label: 'Глубокий', stroke: PHASE_COLORS.deep, width: 2, points: { show: true, size: 4, fill: PHASE_COLORS.deep } },
      { label: 'REM', stroke: PHASE_COLORS.rem, width: 2, points: { show: true, size: 4, fill: PHASE_COLORS.rem } },
      { label: 'Лёгкий', stroke: PHASE_COLORS.light, width: 1.75, points: { show: true, size: 4, fill: PHASE_COLORS.light } },
      { label: 'Бодрствование', stroke: PHASE_COLORS.awake, width: 1.5, points: { show: true, size: 3, fill: PHASE_COLORS.awake } },
    ],
  };

  const data: uPlot.AlignedData = [xs, deep, rem, light, awake];
  const plot = new uPlotCtor(opts, data, area);

  const tooltip = h('div', { class: 'chart-tooltip chart-tooltip--multi' });
  area.appendChild(tooltip);

  const onMove = () => {
    const idx = plot.cursor.idx;
    if (idx == null) {
      tooltip.classList.remove('chart-tooltip--visible');
      return;
    }
    const ts = xs[idx];
    if (ts == null) return;
    // dynamic color from PHASE_COLORS (no CSS tokens defined for sleep phases)
    const lines = [
      `<b>${fullDateRu(ts)}</b>`,
      `<span class="chart-tooltip-dot" style="background:${PHASE_COLORS.deep}"></span> Глубокий: ${deep[idx] ?? '—'}${deep[idx] != null ? ' мин' : ''}`,
      `<span class="chart-tooltip-dot" style="background:${PHASE_COLORS.rem}"></span> REM: ${rem[idx] ?? '—'}${rem[idx] != null ? ' мин' : ''}`,
      `<span class="chart-tooltip-dot" style="background:${PHASE_COLORS.light}"></span> Лёгкий: ${light[idx] ?? '—'}${light[idx] != null ? ' мин' : ''}`,
      `<span class="chart-tooltip-dot" style="background:${PHASE_COLORS.awake}"></span> Бодрствование: ${awake[idx] ?? '—'}${awake[idx] != null ? ' мин' : ''}`,
    ];
    tooltip.innerHTML = lines.join('<br>');
    const x = plot.valToPos(ts, 'x');
    const w = area.clientWidth;
    tooltip.style.left = `${Math.max(8, Math.min(w - 220, x - 110))}px`;
    tooltip.style.top = '8px';
    tooltip.classList.add('chart-tooltip--visible');
  };
  const onLeave = () => tooltip.classList.remove('chart-tooltip--visible');
  plot.over.addEventListener('mousemove', onMove);
  plot.over.addEventListener('mouseleave', onLeave);

  const resize = () => plot.setSize({ width: area.clientWidth, height: area.clientHeight });
  const ro = new ResizeObserver(resize);
  ro.observe(area);
  registerCleanup(() => {
    ro.disconnect();
    plot.over.removeEventListener('mousemove', onMove);
    plot.over.removeEventListener('mouseleave', onLeave);
    plot.destroy();
  });
}

function lastNightCard(last: OuraLastNight | null): HTMLElement {
  if (!last) {
    return h('section', { class: 'sleep-section' }, [
      h('h2', { class: 'sleep-section-header' }, [h('span', null, '🌙 Последняя ночь')]),
      h('p', { class: 'sleep-section-hint' }, 'Нет данных за последнюю ночь.'),
    ]);
  }
  const bed = last.bedtime_start ? formatTime(last.bedtime_start) : '—';
  const wake = last.bedtime_end ? formatTime(last.bedtime_end) : '—';

  const headerLine = h('div', { class: 'sleep-lastnight-line' }, [
    h('span', { class: 'sleep-lastnight-hours' },
      last.total_sleep_hours != null ? `${formatNumber(last.total_sleep_hours, 1)} ч сна` : '—'),
    h('span', { class: 'sleep-lastnight-meta' },
      `${bed} → ${wake}${last.day ? ` · ${formatDate(last.day)}` : ''}`),
  ]);

  const kv: Array<[string, string, string]> = [
    ['Sleep score', last.sleep_score != null ? formatNumber(last.sleep_score, 0) : '—', ''],
    ['Readiness', last.readiness != null ? formatNumber(last.readiness, 0) : '—', ''],
    ['HRV', last.average_hrv != null ? formatNumber(last.average_hrv, 0) : '—', last.average_hrv != null ? 'мс' : ''],
    ['Пульс покоя', last.average_heart_rate != null ? formatNumber(last.average_heart_rate, 0) : '—', last.average_heart_rate != null ? 'уд/мин' : ''],
    ['Эффективность', last.efficiency != null ? formatNumber(last.efficiency, 1) : '—', last.efficiency != null ? '%' : ''],
    ['Латентность', last.latency_minutes != null ? formatNumber(last.latency_minutes, 0) : '—', last.latency_minutes != null ? 'мин' : ''],
    ['Беспокойство', last.restless_periods != null ? String(last.restless_periods) : '—', ''],
    ['Δt°',
      last.temperature_deviation != null
        ? `${last.temperature_deviation > 0 ? '+' : ''}${formatNumber(last.temperature_deviation, 2)}`
        : '—',
      ''],
  ];

  return h('section', { class: 'sleep-section' }, [
    h('h2', { class: 'sleep-section-header' }, [h('span', null, '🌙 Последняя ночь')]),
    headerLine,
    h('div', { class: 'deepdive-stat-grid' },
      kv.map(([label, value, unit]) => DeepDiveTile(label, value, unit))),
    h('div', { class: 'sleep-lastnight-phases' },
      PhasesBar({
        segments: [
          { key: 'deep', label: 'Глубокий', pct: last.deep_pct, minutes: last.deep_minutes, color: PHASE_COLORS.deep },
          { key: 'rem', label: 'REM', pct: last.rem_pct, minutes: last.rem_minutes, color: PHASE_COLORS.rem },
          { key: 'light', label: 'Лёгкий', pct: last.light_pct, minutes: last.light_minutes, color: PHASE_COLORS.light },
          { key: 'awake', label: 'Бодрствование', pct: last.awake_pct, minutes: last.awake_minutes, color: PHASE_COLORS.awake },
        ],
      })),
  ]);
}

function chartsGrid(recent: OuraRecentDay[], window: WindowKey): HTMLElement {
  // parseDate, а не UTC-полночь: подписи осей читают эти значения локальными
  // геттерами (SingleLineChart), поэтому западнее Гринвича день съезжал назад.
  const xs = recent.map((r) => (parseDate(r.day)?.getTime() ?? NaN) / 1000);
  const specs: ChartSpec[] = [
    {
      title: 'Время сна',
      unit: 'часы',
      xs,
      values: recent.map((r) => r.sleep_hours ?? null),
      fractionDigits: 1,
    },
    {
      title: 'Sleep score',
      unit: 'score',
      xs,
      values: recent.map((r) => r.score ?? null),
      fractionDigits: 0,
    },
    {
      title: 'HRV',
      unit: 'мс',
      xs,
      values: recent.map((r) => r.hrv ?? null),
      fractionDigits: 0,
    },
    {
      title: 'Пульс покоя',
      unit: 'уд/мин',
      xs,
      values: recent.map((r) => r.resting_hr ?? null),
      fractionDigits: 0,
    },
  ];

  return chartsSection(specs, window);
}

